import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Badge, Button, HelpIcon, Panel, SectionLabel, Skeleton, Tooltip } from '@/components/ui'
import { fmt, fmtUsd, truncAddr } from '@/lib/utils'
import { usePointsData } from '@/hooks/usePointsData'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/// Ranks every address in scripts/indexPoints.cjs's points snapshot (public/points.json) by
/// total points - own activity plus referral bonus, see that script's own doc comment for the
/// full formula. A fresh build, not a revival of the old, removed LeaderboardPage (that one
/// ranked only top stakers, a narrower and less defensible metric - see CHANGELOG.md item
/// 12/50 area for why it was cut). Each row links straight into that address's read-only
/// Portfolio view (`?view=0x...`, the same URL param PortfolioSection's own AddressSwitcher
/// resolves - see useWatchedAddresses' own doc comment), so anyone can inspect what's actually
/// behind a given rank without needing to connect or manually paste an address.
///
/// UX pass (entry 261): the board carried eleven columns at a 1180px min-width - past the point
/// anyone parses a row at a glance. Trimmed to six primary columns; `Power` already folds
/// time-lock + burn power into one live number, so Locked/Burned/activity/referral breakdowns
/// move onto a per-row expandable detail strip (still there, one tap away, not gone). The
/// connected wallet's own row is tinted and badged, and pinned to the foot of the list when it
/// ranks below the visible top slice, so you can always find yourself without scrolling the
/// whole board. Below 900px the table becomes cards - a horizontal-scroll table on a phone is
/// the worst of both.
const TOP_N = 25

export function LeaderboardSection() {
  const { data, loading } = usePointsData()
  const { address: connected } = useAccount()
  const narrow = useMediaQuery('(max-width: 900px)')
  const [showAll, setShowAll] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())

  const me = connected ? connected.toLowerCase() : null
  const toggle = (addr) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(addr) ? next.delete(addr) : next.add(addr)
      return next
    })

  const entries = data
    ? Object.entries(data.points)
        .map(([address, p]) => ({ address, ...p }))
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .map((e, i) => ({ ...e, rank: i + 1, isMe: me != null && e.address.toLowerCase() === me }))
    : []

  const myIndex = entries.findIndex((e) => e.isMe)
  const visible = showAll ? entries : entries.slice(0, TOP_N)
  const hiddenCount = entries.length - visible.length
  // Pin the connected wallet's own row to the foot when it ranks below the visible slice, so it
  // is always reachable without expanding the whole board. Skipped once every row is shown.
  const pinnedMe = !showAll && myIndex >= visible.length ? entries[myIndex] : null

  return (
    <div id="leaderboard">
      <SectionHeader
        title="Leaderboard"
        subtitle="Every address ranked by points, alongside what each one holds, has earned, and has committed."
      />

      <Panel style={{ padding: 0 }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <SectionLabel>Rankings</SectionLabel>
          {myIndex >= 0 && (
            <span style={{ fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textDim }} className="mono">
              You rank <span style={{ color: T.accent, fontWeight: 700 }}>#{myIndex + 1}</span> of {entries.length}
            </span>
          )}
        </div>

        {loading ? (
          <LoadingRows narrow={narrow} />
        ) : entries.length === 0 ? (
          <div style={emptyCell}>No points data published yet.</div>
        ) : narrow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
            {visible.map((e) => (
              <LeaderboardCard key={e.address} entry={e} open={expanded.has(e.address)} onToggle={() => toggle(e.address)} />
            ))}
            {pinnedMe && (
              <>
                <div style={{ textAlign: 'center', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>· · ·</div>
                <LeaderboardCard entry={pinnedMe} open={expanded.has(pinnedMe.address)} onToggle={() => toggle(pinnedMe.address)} />
              </>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Rank</th>
                  <th style={th}>Address</th>
                  <th style={{ ...th, textAlign: 'right' }}>Points</th>
                  {/* Portfolio value and Total earned measure the PRESENT - what an address holds
                      right now, and what it has actually been paid - where points measure activity
                      integrated over time. Deliberately separate columns rather than folded into
                      the points score: a formula that silently included balance would let one
                      whale outrank sustained participation. */}
                  <th style={{ ...th, textAlign: 'right' }}>
                    <ColLabel text="Portfolio value" tooltip="Money-market position (supplied minus borrowed), staked HORIZON, and Guardian Pool stake, priced when the snapshot was taken. ALM vault shares are not included - valuing them needs each vault's own TWAP ratio, which the indexer has no equivalent of." />
                  </th>
                  <th style={{ ...th, textAlign: 'right' }}>
                    <ColLabel text="Total earned" tooltip="Lifetime HORIZON this address has actually claimed, across money-market incentives and vault-share emissions. Pending-but-unclaimed rewards are counted once claimed." />
                  </th>
                  {/* Power folds the two commitment signals (Locked + Burned) into one live number,
                      so it stands in for both on the primary board; the raw Locked/Burned figures
                      live on the expand strip. */}
                  <th style={{ ...th, textAlign: 'right' }}>
                    <ColLabel text="Power" tooltip="Live voting weight: time-lock power (declining toward each unlock date) plus permanent burn power. Expand a row for the raw Locked and Burned amounts behind it." />
                  </th>
                  <th style={{ ...th, width: 40 }} aria-label="Expand" />
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <LeaderboardRow key={e.address} entry={e} open={expanded.has(e.address)} onToggle={() => toggle(e.address)} />
                ))}
                {pinnedMe && (
                  <>
                    <tr>
                      <td colSpan={7} style={{ ...td, textAlign: 'center', color: T.textDim, padding: '8px 24px' }}>· · ·</td>
                    </tr>
                    <LeaderboardRow entry={pinnedMe} open={expanded.has(pinnedMe.address)} onToggle={() => toggle(pinnedMe.address)} />
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && hiddenCount > 0 && (
          <div style={{ padding: '16px 24px 20px', display: 'flex', justifyContent: 'center' }}>
            <Button variant="ghost" onClick={() => setShowAll(true)}>
              Show all {entries.length} addresses
            </Button>
          </div>
        )}
        {!loading && showAll && entries.length > TOP_N && (
          <div style={{ padding: '16px 24px 20px', display: 'flex', justifyContent: 'center' }}>
            <Button variant="ghost" onClick={() => setShowAll(false)}>
              Show top {TOP_N} only
            </Button>
          </div>
        )}
      </Panel>
    </div>
  )
}

/// Column header with an explanation attached. Each surviving column has a real, non-obvious
/// exclusion or composition worth a line a reader can compare themselves against.
function ColLabel({ text, tooltip }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
      {text}
      <Tooltip content={<div style={{ maxWidth: 240, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5, whiteSpace: 'normal', textTransform: 'none', letterSpacing: 0 }}>{tooltip}</div>}>
        {/* nativeTitle off - the Tooltip already renders this string, and the browser bubble would
            draw a duplicate beside it (CHANGELOG.md entry 72). */}
        <HelpIcon text={tooltip} nativeTitle={false} />
      </Tooltip>
    </span>
  )
}

function RankCell({ rank }) {
  return (
    <span style={{ color: rank <= 3 ? T.accent : T.text, fontWeight: rank <= 3 ? 800 : 400 }} className="mono">
      #{rank}
    </span>
  )
}

function AddrCell({ entry }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Link to={`/portfolio?view=${entry.address}`} style={addrLink} className="mono">
        {truncAddr(entry.address)}
      </Link>
      {entry.isMe && <Badge color={T.accent}>You</Badge>}
    </span>
  )
}

/// `?? null` throughout rather than `?? 0`: a snapshot written before a field existed reads "—",
/// never a confident $0.00 for every address on the board.
const dash = (v, render) => (v != null ? render(v) : '—')

function LeaderboardRow({ entry, open, onToggle }) {
  const rowBg = entry.isMe ? { background: T.accentDim } : null
  return (
    <>
      <tr style={rowBg}>
        <td style={td}><RankCell rank={entry.rank} /></td>
        <td style={td}><AddrCell entry={entry} /></td>
        <td style={{ ...td, textAlign: 'right' }} className="mono">{fmt(entry.totalPoints, 2)}</td>
        <td style={{ ...td, textAlign: 'right' }} className="mono">{dash(entry.portfolioUsd, fmtUsd)}</td>
        <td style={{ ...td, textAlign: 'right', color: entry.totalEarnedHorizon > 0 ? T.positive : T.textDim }} className="mono">
          {dash(entry.totalEarnedHorizon, (v) => `${fmt(v, 2)} HORIZON`)}
        </td>
        <td style={{ ...td, textAlign: 'right', color: entry.vePower > 0 ? T.positive : T.textDim }} className="mono">
          {dash(entry.vePower, (v) => fmt(v, 2))}
        </td>
        <td style={{ ...td, textAlign: 'right', padding: '14px 16px' }}>
          <ExpandToggle open={open} onClick={onToggle} />
        </td>
      </tr>
      {open && (
        <tr style={rowBg}>
          <td colSpan={7} style={{ ...td, paddingTop: 4, paddingBottom: 18 }}>
            <DetailGrid entry={entry} />
          </td>
        </tr>
      )}
    </>
  )
}

function LeaderboardCard({ entry, open, onToggle }) {
  return (
    <div style={{ border: `1px solid ${entry.isMe ? T.accent : T.border}`, background: entry.isMe ? T.accentDim : T.surface, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <RankCell rank={entry.rank} />
          <AddrCell entry={entry} />
        </span>
        <span style={{ textAlign: 'right' }} className="mono">
          <div style={{ fontSize: T.fs.heading, color: T.text, fontWeight: 700 }}>{fmt(entry.totalPoints, 2)}</div>
          <div style={{ fontSize: T.fs.caption, color: T.textDim, fontFamily: T.fontM }}>points</div>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
        <MiniStat label="Portfolio" value={dash(entry.portfolioUsd, fmtUsd)} />
        <MiniStat label="Earned" value={dash(entry.totalEarnedHorizon, (v) => fmt(v, 2))} color={entry.totalEarnedHorizon > 0 ? T.positive : undefined} />
        <MiniStat label="Power" value={dash(entry.vePower, (v) => fmt(v, 2))} color={entry.vePower > 0 ? T.positive : undefined} />
      </div>
      <button onClick={onToggle} style={cardExpandBtn} className="mono">
        {open ? 'Hide breakdown' : 'Show breakdown'}
      </button>
      {open && <div style={{ marginTop: 12 }}><DetailGrid entry={entry} /></div>}
    </div>
  )
}

/// The detail strip: the figures cut from the primary view. Locked/Burned are the raw commitment
/// numbers Power folds together; the activity/referral split and referral count show where the
/// points score itself comes from.
function DetailGrid({ entry }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 14 }}>
      <DetailItem label="Locked" tooltip="HORIZON currently time-locked in the vote-escrow, earning the protocol revenue share. Snapshot at index time." value={dash(entry.lockedHorizon, (v) => fmt(v, 2))} strong={entry.lockedHorizon > 0} />
      <DetailItem label="Burned" tooltip="HORIZON burned for 2x its amount in permanent power. The strongest commitment on the board: it only grows." value={dash(entry.burnedHorizon, (v) => fmt(v, 2))} accent={entry.burnedHorizon > 0} />
      <DetailItem label="From activity" tooltip="Points earned from this address's own protocol activity, before any referral bonus." value={fmt(entry.ownPoints, 2)} />
      <DetailItem label="From referrals" tooltip="Bonus points credited from addresses this one referred." value={fmt(entry.referralBonusPoints, 2)} />
      <DetailItem label="Referred" tooltip="Number of addresses this one has referred." value={String(entry.referralCount)} />
    </div>
  )
}

function DetailItem({ label, tooltip, value, strong, accent }) {
  return (
    <div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
        <Tooltip content={<div style={{ maxWidth: 220, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5, whiteSpace: 'normal', textTransform: 'none', letterSpacing: 0 }}>{tooltip}</div>}>
          <HelpIcon text={tooltip} nativeTitle={false} />
        </Tooltip>
      </div>
      <div className="mono" style={{ marginTop: 4, fontSize: T.fs.ui, color: accent ? T.accent : strong ? T.text : T.textMuted, fontWeight: accent ? 700 : 400 }}>
        {value}
      </div>
    </div>
  )
}

function MiniStat({ label, value, color }) {
  return (
    <div>
      <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div className="mono" style={{ marginTop: 3, fontSize: T.fs.ui, color: color || T.text }}>{value}</div>
    </div>
  )
}

function ExpandToggle({ open, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={open ? 'Hide breakdown' : 'Show breakdown'}
      aria-expanded={open}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', color: T.textDim, padding: 4,
        display: 'inline-flex', transition: 'transform 0.15s ease, color 0.15s ease',
        transform: open ? 'rotate(180deg)' : 'none',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = T.accent }}
      onMouseLeave={(e) => { e.currentTarget.style.color = T.textDim }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

function LoadingRows({ narrow }) {
  const rows = Array.from({ length: 6 })
  if (narrow) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
        {rows.map((_, i) => (
          <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
            <Skeleton width={180} height={16} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
              {[0, 1, 2].map((j) => <Skeleton key={j} width={60} height={14} />)}
            </div>
          </div>
        ))}
      </div>
    )
  }
  return (
    <div style={{ padding: '8px 24px 20px' }}>
      {rows.map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '12px 0', borderBottom: `1px solid ${T.border}` }}>
          <Skeleton width={32} height={14} />
          <Skeleton width={120} height={14} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 40 }}>
            {[0, 1, 2, 3].map((j) => <Skeleton key={j} width={70} height={14} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

const table = { width: '100%', borderCollapse: 'collapse', minWidth: 720 }
const th = {
  textAlign: 'left', padding: '12px 24px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
  fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`,
}
const td = {
  padding: '14px 24px', borderBottom: `1px solid ${T.border}`, color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui,
}
const addrLink = { color: T.accent, textDecoration: 'none' }
const emptyCell = {
  padding: '32px 24px', textAlign: 'center', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body,
}
const cardExpandBtn = {
  marginTop: 12, width: '100%', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8,
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, padding: '8px 0', cursor: 'pointer',
}
